import 'date-fns';
import React, { FC } from 'react';
import { InputAdornment, TextField, Theme } from '@mui/material';
import { Lock, DateRange } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import {
  Controller,
  UseControllerProps,
  useFormContext,
} from 'react-hook-form';
import { createStyles, makeStyles } from '@mui/styles';

const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    noBorder: {
      border: 'none',
    },
    noTopBottomPadding: {
      paddingTop: '0px',
      paddingBottom: '0px',
    },
  })
);

export interface RHFDataPickerProps {
  name: string;
  label: string;
  variant?: 'dialog' | 'inline' | 'static';
  minDate?: Date;
  rules?: UseControllerProps['rules'];
  required?: boolean;
  disabled?: boolean;
  isTableCellInput?: boolean;
  onChangeHandler?: Function;
  RHFChangeHandler?: (e: Date) => any;
  getDisplayValue?: (e: any) => string;
  onBlurExternal?: () => void;
}

// RHFChangeHandler will take the newDate option and then map it to desired value
// getDisplayValue will take the value from the form and show how it was displayed.
export const RHFDatePicker: FC<RHFDataPickerProps> = ({
  name,
  label,
  minDate = new Date('2022-01-01'),
  rules,
  required = false,
  disabled = false,
  isTableCellInput = false,
  onChangeHandler = null,
  RHFChangeHandler = e => e,
  getDisplayValue = e => e,
  onBlurExternal = () => {},
}: RHFDataPickerProps) => {
  const methods = useFormContext();
  const classes = useStyles();

  let localInputProps;
  if (disabled) {
    localInputProps = {
      endAdornment: (
        <InputAdornment position="end">
          <Lock />
        </InputAdornment>
      ),
    };
  } else {
    localInputProps = {
      endAdornment: (
        <InputAdornment position="end">
          <DateRange />
        </InputAdornment>
      ),
    };
  }

  if (isTableCellInput) {
    localInputProps.classes = {
      notchedOutline: classes.noBorder,
      input: classes.noTopBottomPadding,
    };
  }

  return (
    <Controller
      name={name}
      control={methods.control}
      rules={rules}
      render={({
        field: { onChange, onBlur, value, ref: fieldRef },
        fieldState: { error },
      }) => {
        return (
          <DatePicker
            renderInput={props => {
              return (
                <TextField
                  {...props}
                  error={!!error}
                  fullWidth
                  required={required}
                  onBlur={() => {
                    onBlur();
                    onBlurExternal();
                  }}
                />
              );
            }}
            onChange={date => {
              onChange(RHFChangeHandler(date as any));
              if (!!onChangeHandler) {
                onChangeHandler(date);
              }
            }}
            label={label}
            value={getDisplayValue(value)}
            InputProps={localInputProps}
            inputRef={fieldRef}
            minDate={minDate}
            disabled={disabled}
          />
        );
      }}
    />
  );
};
